import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import InfirmationTable from "../components/InfirmationTable";
import HeaderTablePage from "../components/HeaderTablePage";
import EmptyTable from "../components/EmptyTable";
import HtmlDescription from "../components/DescriptionDetail/HtmlDescription";
import HtmlDescriptionModal from "../components/DescriptionDetail/HtmlDescriptionModal";
import Pagination from "../components/Pagination";
import axios from "axios";
import { verifyPermission } from "../../helpers/permission";
import appConfig from "../../config/appConfig";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { useTranslation } from "react-i18next";
import { formatDateToTime, loadCss, forceRound, showDatePassed } from "../../helpers/function";
import Loader from "../components/Loader";
import { NUMBER_ELEMENT_PER_PAGE } from "../../constants/dataTable";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

/** Mapping des statuts API vers les clés d’affichage du suivi */
const STATUS_TO_LABEL_KEY = {
  to_assign: "toAssignementToStaff",
  transferred_to_unit: "toAssignementToStaff",
  assigned_to_staff: "awaitingTreatment",
  awaiting_treatment: "awaitingTreatment",
  solved: "toValidate",
  treated: "toValidate",
  to_validate: "toValidate",
  validated: "toMeasureSatisfaction",
  to_measure_satisfaction: "toMeasureSatisfaction",
};

const ClaimMonitoringSuivi = (props) => {
  const { t, ready } = useTranslation();

  document.title = "Satis client - " + (ready ? t("Suivi des réclamations") : "");

  const [stats, setStats] = useState({
    toAssignementToStaff: 0,
    awaitingTreatment: 0,
    toValidate: 0,
    toMeasureSatisfaction: 0,
  });
  const [claims, setClaims] = useState([]);
  const [isLoad, setIsLoad] = useState(true);
  const [currentMessage, setCurrentMessage] = useState("");
  const [search, setSearch] = useState("");
  const [activeNumberPage, setActiveNumberPage] = useState(1);
  const [numberPerPage, setNumberPerPage] = useState(NUMBER_ELEMENT_PER_PAGE);
  const [numberPage, setNumberPage] = useState(0);
  const [total, setTotal] = useState(0);

  const statusLabel = useCallback(
    (key) => {
      const labels = {
        toAssignementToStaff: t("En attente d'affectation à un staff"),
        awaitingTreatment: t("Assignées en attente de traitement"),
        toValidate: t("Traitées en attente de validation"),
        toMeasureSatisfaction: t("Validées en attente de mesure de satisfaction"),
      };
      return labels[key] || key;
    },
    [t]
  );

  const getStatusLabelForKey = useCallback(
    (apiStatus) => {
      const key = apiStatus ? STATUS_TO_LABEL_KEY[apiStatus] : null;
      return key ? statusLabel(key) : (apiStatus || "-");
    },
    [statusLabel]
  );

  const showModal = (message) => {
    setCurrentMessage(message);
    document.getElementById("button_modal").click();
  };

  const printBodyTable = (claim, index) => {
    const description =
      claim.plain_text_description != null
        ? claim.plain_text_description
        : claim.description;
    return (
      <tr key={`${claim.id}-${index}`} role="row" className="odd">
        <td>{claim.reference || "-"}</td>
        <td>
          {claim.claimer?.raison_sociale
            ? claim.claimer.raison_sociale
            : [claim.claimer?.lastname, claim.claimer?.firstname].filter(Boolean).join(" ") || "-"}
          {claim.account_targeted
            ? " / " + claim.account_targeted.number
            : claim.account_number
              ? " / " + claim.account_number
              : ""}
        </td>
        <td>{claim.received_at ? formatDateToTime(claim.received_at) : "-"}<br />
          {showDatePassed(claim)}
        </td>
        <td>{claim.created_at ? formatDateToTime(claim.created_at) : "-"}</td>
        <td>{claim.claim_object?.name?.fr ?? "-"}</td>
        <td style={{ textAlign: "center" }}>
          <HtmlDescription
            onClick={() => showModal(description != null ? description : "-")}
          />
        </td>
        <td>{getStatusLabelForKey(claim.status)}</td>
        <td className="text-center">
          <a
            href={`/process/claim-list-detail/${claim.id}/detail`}
            className="btn btn-sm btn-clean btn-icon btn-icon-md"
            title={t("Détails")}
          >
            <i className="la la-eye" />
          </a>
        </td>
      </tr>
    );
  };

  const getEndpoint = useCallback(() => {
    if (props.plan === "MACRO") {
      if (
        verifyPermission(props.userPermissions, "show-any-staff-monitoring")
      )
        return `${appConfig.apiDomaine}/any/monitoring-claim-lead`;
      if (
        verifyPermission(props.userPermissions, "show-my-staff-monitoring")
      )
        return `${appConfig.apiDomaine}/my/monitoring-claim-lead`;
    }
    if (props.plan === "HUB")
      return `${appConfig.apiDomaine}/any/monitoring-claim-lead`;
    if (props.plan === "PRO")
      return `${appConfig.apiDomaine}/my/monitoring-claim-lead`;
    return "";
  }, [props.plan, props.userPermissions]);

  useEffect(() => {
    const endpoint = getEndpoint();
    if (!endpoint || !verifyTokenExpire()) return;

    async function fetchData() {
      setIsLoad(true);
      const params = new URLSearchParams();
      params.set("page", String(activeNumberPage));
      params.set("size", String(numberPerPage));
      if (search.trim()) params.set("search", search.trim());
      const url = `${endpoint}?${params.toString()}`;

      try {
        const response = await axios.get(url);
        const data = response.data;

        setStats({
          toAssignementToStaff: Number(data.toAssignementToStaff) || 0,
          awaitingTreatment: Number(data.awaitingTreatment) || 0,
          toValidate: Number(data.toValidate) || 0,
          toMeasureSatisfaction: Number(data.toMeasureSatisfaction) || 0,
        });

        const claimsPayload = data.claims;
        const claimsList =
          claimsPayload && Array.isArray(claimsPayload.data)
            ? claimsPayload.data
            : [];
        const totalCount =
          claimsPayload && claimsPayload.total != null
            ? Number(claimsPayload.total)
            : 0;
        const lastPage =
          claimsPayload && claimsPayload.last_page != null
            ? Number(claimsPayload.last_page)
            : totalCount > 0
              ? forceRound(totalCount / numberPerPage)
              : 0;

        setClaims(claimsList);
        setTotal(totalCount);
        setNumberPage(lastPage);
      } catch (error) {
        console.error("Erreur lors de la récupération des statistiques:", error);
        setClaims([]);
        setTotal(0);
        setNumberPage(0);
      } finally {
        setIsLoad(false);
      }
    }

    fetchData();
  }, [
    getEndpoint,
    activeNumberPage,
    numberPerPage,
    search,
  ]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setActiveNumberPage(1);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    setActiveNumberPage(1);
  };

  const handlePageChange = (e, page) => {
    e?.preventDefault();
    setActiveNumberPage(page);
  };

  const handlePreviousPage = (e) => {
    e?.preventDefault();
    if (activeNumberPage > 1) setActiveNumberPage(activeNumberPage - 1);
  };

  const handleNextPage = (e) => {
    e?.preventDefault();
    if (activeNumberPage < numberPage) setActiveNumberPage(activeNumberPage + 1);
  };

  const handleNumberPerPageChange = (e) => {
    const value = parseInt(e.target.value, 10) || NUMBER_ELEMENT_PER_PAGE;
    setNumberPerPage(value);
    setActiveNumberPage(1);
  };

  return ready ? (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader   kt-grid__item" id="kt_subheader">
        <div className="kt-container  kt-container--fluid ">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Suivi des réclamations")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
              >
                {t("Suivi")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        <InfirmationTable
          information={t(
            "Cette interface affiche les statistiques de suivi des réclamations"
          )}
        />

        <div className="kt-portlet">
          <HeaderTablePage title={t("Statistiques de suivi des réclamations")} />

          <div className="kt-portlet__body">
            <div className="row mb-4">
              <div className="col-sm-12 col-md-6">
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("Rechercher (référence, réclamant, objet…)")}
                    value={search}
                    onChange={handleSearchChange}
                    aria-label={t("Recherche")}
                  />
                </form>
              </div>
            </div>
            {isLoad ? (
              <div className="position-relative">
                <Loader />
              </div>
            ) : (
              <>
              <div className="row row-no-padding row-col-separator-lg">
                <div className="col-md-12 col-lg-3 col-xl-3">
                  <div className="kt-widget24">
                    <div className="kt-widget24__details" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                      <div className="kt-widget24__info">
                        <h5 className="kt-widget24__title">
                          {t("En attente d'affectation à un staff")}
                        </h5>
                        <span className="kt-widget24__desc"></span>
                      </div>
                      <span className="kt-widget24__stats kt-font-warning">
                        {stats.toAssignementToStaff}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 col-lg-3 col-xl-3">
                  <div className="kt-widget24">
                    <div className="kt-widget24__details" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                      <div className="kt-widget24__info">
                        <h5 className="kt-widget24__title">
                          {t("Assignées en attente de traitement")}
                        </h5>
                        <span className="kt-widget24__desc"></span>
                      </div>
                      <span className="kt-widget24__stats kt-font-info">
                        {stats.awaitingTreatment}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 col-lg-3 col-xl-3">
                  <div className="kt-widget24">
                    <div className="kt-widget24__details" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                      <div className="kt-widget24__info">
                        <h5 className="kt-widget24__title">
                          {t("Traitées en attente de validation")}
                        </h5>
                        <span className="kt-widget24__desc"></span>
                      </div>
                      <span className="kt-widget24__stats kt-font-success">
                        {stats.toValidate}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 col-lg-3 col-xl-3">
                  <div className="kt-widget24">
                    <div className="kt-widget24__details" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                      <div className="kt-widget24__info">
                        <h5 className="kt-widget24__title">
                          {t("Validées en attente de mesure de satisfaction")}
                        </h5>
                        <span className="kt-widget24__desc"></span>
                      </div>
                      <span className="kt-widget24__stats kt-font-primary">
                        {stats.toMeasureSatisfaction}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div
                  id="kt_table_1_wrapper"
                  className="dataTables_wrapper dt-bootstrap4"
                >
                  <div className="row">
                    <div className="col-sm-12">
                      <table
                        className="table table-striped table-bordered table-hover table-checkable dataTable dtr-inline"
                        id="myTable"
                        role="grid"
                        aria-describedby="kt_table_1_info"
                      >
                        <thead>
                          <tr role="row">
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Référence")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Réclamant")}
                            </th>
                            <th
                              className="sorting sorter-dates"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Date de réception")}
                            </th>
                            <th
                              className="sorting sorter-dates"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "50px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Date de création")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Objet de réclamation")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Description")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Country: activate to sort column ascending"
                            >
                              {t("Statut")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "40.25px" }}
                              aria-label="Type: activate to sort column ascending"
                            >
                              {t("Action")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {claims.length > 0 ? (
                            claims.map((claim, index) =>
                              printBodyTable(claim, index)
                            )
                          ) : (
                            <EmptyTable colSpan={8} />
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th rowSpan="1" colSpan="1">
                              {t("Référence")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Réclamant")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Date de réception")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Date de création")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Objet de réclamation")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Description")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Statut")}
                            </th>
                            <th rowSpan="1" colSpan="1">
                              {t("Action")}
                            </th>
                          </tr>
                        </tfoot>
                      </table>
                      <button
                        id="button_modal"
                        type="button"
                        className="btn btn-secondary btn-icon-sm d-none"
                        data-toggle="modal"
                        data-target="#message_email"
                      />
                      <HtmlDescriptionModal
                        title={t("Description")}
                        message={currentMessage}
                      />
                    </div>
                  </div>
                  <div className="row align-items-center flex-wrap">
                    <div className="col-sm-12 col-md-5">
                      <div
                        className="dataTables_info"
                        id="kt_table_1_info"
                        role="status"
                        aria-live="polite"
                      >
                        {total > 0
                          ? `${t("Affichage de")} ${(activeNumberPage - 1) * numberPerPage + 1} ${t("à")} ${Math.min(activeNumberPage * numberPerPage, total)} ${t("sur")} ${total} ${t("données")}`
                          : `${t("Affichage de")} 0 ${t("sur")} 0 ${t("données")}`}
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-7 d-flex justify-content-end">
                      <Pagination
                        numberPerPage={numberPerPage}
                        activeNumberPage={activeNumberPage}
                        numberPage={numberPage}
                        onClickPage={handlePageChange}
                        onClickPreviousPage={handlePreviousPage}
                        onClickNextPage={handleNextPage}
                        onChangeNumberPerPage={handleNumberPerPageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
    plan: state.plan.plan,
  };
};

export default connect(mapStateToProps)(ClaimMonitoringSuivi);
